import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next";
import { withPage } from "src/utils/pagination/with-page";
import { HomeView } from "src/organisms/HomeView";
import getConfig from "next/config";
import {
  Homepage,
  NavigationItems,
  SeoQueryResponse,
} from "src/types/graphql.d";
import { getHomepage } from "src/services/graphql/get-homepage";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { getNavigationItems } from "src/services/graphql/get-navigation-items";
import { getSeoContent } from "src/services/graphql/get-seo-content";
import { getCategoryProductListProps } from "src/utils/search-provider/get-product-list-props";
import { buildStaticProps } from "src/utils/static-props/app-page-props";
import { PRODUCT_LIST_TYPE } from "src/utils/constants";
import type {
  CategoryProductList,
  ProductListProps,
} from "src/types/product-listing";
import { ProductListingView } from "src/organisms/ProductListingView";

type HomePageProps = {
  pageProps:
    | {
        homepage: Homepage;
        slug: string;
        locale: string;
        navigationItems?: NavigationItems | null;
        gender: string;
        seoContent?: SeoQueryResponse | null;
      }
    | ProductListProps<CategoryProductList>;
};

const { serverRuntimeConfig } = getConfig();

const genderTranslations = [
  "women",
  "femme",
  "damen",
  "dames",
  "kobiety",
  "mujer",
  "donna",
  "dame",
  "kvinna",
  "kids",
  "enfant",
  "kinder",
  "kids",
  "ninos",
  "bambini",
  "dzieci",
  "born",
  "barn",
  "men",
  "harren",
  "herren",
  "homme",
  "heren",
  "hombre",
  "uomo",
  "man",
  "mezczyzni",
];

const allBrandsTranslations = [
  "brands",
  "marques",
  "marken",
  "merken",
  "marki",
  "marcas",
  "marchi",
  "brand",
  "maerker",
  "varumarken",
];

function isCategoryPage(
  pageProps:
    | {
        homepage: Homepage;
        slug: string;
        locale: string;
        navigationItems?: NavigationItems | null;
        gender: string;
      }
    | ProductListProps<CategoryProductList>
): pageProps is ProductListProps<CategoryProductList> {
  return "categoryId" in pageProps;
}

function HomePage({ pageProps }: HomePageProps): React.ReactElement {
  if (isCategoryPage(pageProps)) {
    return <ProductListingView {...pageProps} defaultSorting="POPULARITY" />;
  }

  return (
    <HomeView homepage={pageProps.homepage} seoContent={pageProps.seoContent} />
  );
}

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export const getStaticProps = withPage(async function getStaticProps(
  {
    params,
  }: GetStaticPropsContext<{
    slug: string;
  }>,
  page
): Promise<GetStaticPropsResult<HomePageProps["pageProps"]>> {
  if (!params || !params.slug) {
    return {
      notFound: true,
    };
  }

  const isAllBrands = allBrandsTranslations.includes(params.slug);
  const isValidGender = genderTranslations.includes(params.slug);

  if (!isValidGender) {
    return getCategoryProductListProps({
      type: PRODUCT_LIST_TYPE.CATEGORY_PLP,
      slug: params.slug,
      page,
    });
  }

  if ((!isAllBrands && !isValidGender) || !isValidGender) {
    return {
      notFound: true,
    };
  }

  const { navigationItems } =
    (await getNavigationItems(localeFix({ gender: params.slug }))) || [];

  const [homepage, seoContent] = await Promise.all([
    getHomepage({ gender: localeFix({ gender: params.slug }) }),
    getSeoContent(`/${params.slug}`),
  ]);

  if (!homepage) {
    return {
      notFound: true,
    };
  }

  return buildStaticProps(
    {
      slug: params.slug,
      locale: serverRuntimeConfig.LOCALE,
      navigationItems,
      homepage: homepage || null,
      gender: params.slug,
      seoContent,
    },
    60 * 30
  );
});

export default HomePage;
